import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Modal,
  Paper,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Typography,
  Popover,
  Checkbox,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { Tooltip } from "@mui/material";
import {
  createTransporter,
  deleteTransporter,
  getAllTransporter,
  updateTrasnporter,
  ApproveTransporter,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { CircularProgress } from "@mui/material";

const Transporter = () => {
  const [gstError, setGstError] = useState("");
  const [panError, setPanError] = useState("");
  const [ifscError, setIfscError] = useState("");
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [transporters, setTransporters] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ NEW - Approve/Reject Modal States
  const [openApproveRejectModal, setOpenApproveRejectModal] = useState(false);
  const [transporterToApprove, setTransporterToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null); // "Approved" | "Rejected"

  const [viewOpen, setViewOpen] = useState(false);
  const [viewTransporter, setViewTransporter] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedTransporter, setExpandedTransporter] = useState(null);

  const [formData, setFormData] = useState({
    transporter_name: "",
    contact: "",
    gst_no: "",
    pan_no: "",
    account_no: "",
    ifsc_code: "",
    bank_name: "",
    branch_name: "",
    address: "",
  });

  const [filters, setFilters] = useState({
    transporter_name: "",
    account_no: "",
    pan_no: "",
  });

  const fetchData = async (pageNo = 1, reset = false) => {
    const response = await getAllTransporter(pageNo, filters);
    if (!response) return;

    const incoming = response.results ?? [];
    if (reset) {
      setTransporters(incoming);
    } else {
      setTransporters((prev) => {
        const byId = new Map(prev.map((x) => [x.id, x]));
        for (const item of incoming) byId.set(item.id, item);
        return Array.from(byId.values());
      });
    }
    setHasMore(response.next !== null);
    setPage(pageNo);
  };

  useEffect(() => {
    fetchData(1, true);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData(1, true);
    }, 500);
    return () => clearTimeout(handler);
  }, [filters]);

  const handleOpen = () => {
    setFormData({
      transporter_name: "",
      contact: "",
      gst_no: "",
      pan_no: "",
      account_no: "",
      ifsc_code: "",
      bank_name: "",
      branch_name: "",
      address: "",
    });
    setGstError("");
    setPanError("");
    setIfscError("");
    setEditMode(false);
    setEditingId(null);
    setTransporterToApprove(null); // ✅ Clear approval state
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setEditingId(null);
    setTransporterToApprove(null);
  };

  const handleViewOpen = (transporter) => {
    setViewTransporter(transporter);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewTransporter(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isValidGST = (gst) =>
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);
  const isValidPAN = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
  const isValidIFSC = (ifsc) => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);

  const validateForm = () => {
    const errors = {};
    if (!formData.bank_name) errors.bank_name = "Bank name is required";
    if (!formData.branch_name) errors.branch_name = "Branch name is required";
    if (!formData.address) errors.address = "Address is required";
    if (!formData.ifsc_code) errors.ifsc_code = "IFSC code is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidGST(formData.gst_no)) {
      setGstError("Invalid GST format (e.g., 22AAAAA0000A1Z5)");
      return;
    } else setGstError("");

    if (!isValidPAN(formData.pan_no)) {
      setPanError("Invalid PAN format (e.g., AAAAA1234A)");
      return;
    } else setPanError("");

    if (formData.ifsc_code && !isValidIFSC(formData.ifsc_code)) {
      setIfscError("Invalid IFSC (e.g., SBIN0001234)");
      return;
    } else setIfscError("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      if (editMode && editingId != null) {
        await updateTrasnporter(editingId, formData);
        toast.success("Transporter updated successfully!");
      } else {
        await createTransporter(formData);
        toast.success("Transporter added successfully!");
      }

      await fetchData(1, true);
      handleClose();
    } catch (err) {
      toast.error("Failed to save transporter.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransporter = async (id) => {
    try {
      await deleteTransporter(id);
      toast.success("Transporter deleted successfully!");
      await fetchData(1, true);
    } catch (error) {
      toast.error("Failed to delete transporter");
    }
  };

  const handleEditTransporter = (id) => {
    const transporter = transporters.find((t) => t.id === id);
    if (!transporter) return;
    setFormData({ ...transporter });
    setEditingId(id);
    setEditMode(true);
    setTransporterToApprove(null); // ✅ Clear approval state for normal edit
    setOpen(true);
  };

  // ✅ UPDATED - Approve/Reject handler
  const handleApproveReject = async () => {
    if (!remarkInput.trim()) {
      toast.error("Remark is required!");
      return;
    }

    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveTransporter(
        transporterToApprove.id,
        approvedById,
        actionType,
        remarkInput
      );

      setTransporters((prev) =>
        prev.map((item) =>
          item.id === transporterToApprove.id ? { ...item, ...updated } : item
        )
      );

      const msg =
        actionType === "Approved"
          ? "Transporter approved successfully!"
          : "Transporter rejected successfully!";
      toast.success(msg);

      // ✅ Close modals and reset
      setOpenApproveRejectModal(false);
      setOpen(false);
      setRemarkInput("");
      setActionType(null);
      setTransporterToApprove(null);
    } catch (error) {
      toast.error("Failed to toggle transporter approval.");
    }
  };

  const filteredTransporters = useMemo(() => {
    return transporters.filter(
      (t) =>
        (t.transporter_name || "")
          .toLowerCase()
          .includes((filters.transporter_name || "").toLowerCase()) &&
        (t.account_no || "")
          .toString()
          .toLowerCase()
          .includes((filters.account_no || "").toLowerCase()) &&
        (t.pan_no || "")
          .toLowerCase()
          .includes((filters.pan_no || "").toLowerCase())
    );
  }, [transporters, filters]);

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  // ✅ Mobile Card Component
  const MobileTransporterCard = ({ transporter, index }) => {
    const isExpanded = expandedTransporter === transporter.id;

    return (
      <Box key={transporter.id}>
        <Box
          onClick={() =>
            setExpandedTransporter(
              expandedTransporter === transporter.id ? null : transporter.id
            )
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${transporter.status === "Approved"
                ? "#4caf50"
                : transporter.status === "Rejected"
                  ? "#f44336"
                  : "#2196f3"
              }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                #{index + 1}
              </Typography>
              <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                {transporter.transporter_name}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>{transporter.contact}</span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>{transporter.pan_no}</span>
            </Typography>
          </Box>

          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${transporter.status === "Approved"
                  ? "#4caf50"
                  : transporter.status === "Rejected"
                    ? "#f44336"
                    : "#2196f3"
                }`,
            }}
          >
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.8, mb: 1 }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  TRANSPORTER NO
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {transporter.id}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  GST NO
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {transporter.gst_no || "—"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  ACCOUNT NO
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {transporter.account_no || "—"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  IFSC CODE
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {transporter.ifsc_code || "—"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  BANK NAME
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {transporter.bank_name || "—"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}
                >
                  BRANCH
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                  {transporter.branch_name || "—"}
                </Typography>
              </Box>
            </Box>

            {transporter.address && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  ADDRESS
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}>
                  {transporter.address}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* ✅ Checkbox - Opens edit modal in approval mode */}
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      transporter.status === "Approved"
                        ? "#e8f5e9"
                        : transporter.status === "Rejected"
                          ? "#ffebee"
                          : "#e3f2fd",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${transporter.status === "Approved"
                        ? "#4caf50"
                        : transporter.status === "Rejected"
                          ? "#f44336"
                          : "#2196f3"
                      }`,
                  }}
                >
                  <Checkbox
                    checked={
                      transporter.status === "Approved" ||
                      transporter.status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setFormData({ ...transporter });
                      setEditingId(transporter.id);
                      setEditMode(true);
                      setTransporterToApprove(transporter);
                      setOpen(true);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.75)",
                      color:
                        transporter.status === "Approved"
                          ? "#4caf50"
                          : transporter.status === "Rejected"
                            ? "#f44336"
                            : "#2196f3",
                      "&.Mui-checked": {
                        color:
                          transporter.status === "Approved" ? "#4caf50" : "#f44336",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        transporter.status === "Approved"
                          ? "#2e7d32"
                          : transporter.status === "Rejected"
                            ? "#c62828"
                            : "#1976d2",
                    }}
                  >
                    {transporter.status === "Approved"
                      ? "Approved"
                      : transporter.status === "Rejected"
                        ? "Rejected"
                        : "Approve"}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* Edit - Normal mode */}
                <IconButton
                  size="small"
                  disabled={transporter.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTransporter(transporter.id);
                  }}
                  sx={{
                    bgcolor:
                      transporter.status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        transporter.status === "Approved" ? "#f5f5f5" : "#c8e6c9",
                      transform:
                        transporter.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Edit
                    sx={{
                      fontSize: "1rem",
                      color:
                        transporter.status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  disabled={transporter.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(e.currentTarget);
                    setSelectedId(transporter.id);
                  }}
                  sx={{
                    bgcolor:
                      transporter.status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        transporter.status === "Approved" ? "#f5f5f5" : "#ffcdd2",
                      transform:
                        transporter.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Delete
                    sx={{
                      fontSize: "1rem",
                      color:
                        transporter.status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>

                {/* View */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewOpen(transporter);
                  }}
                  sx={{
                    bgcolor: "#e3f2fd",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: "#bbdefb",
                      transform: "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={2000} />
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            gap: 2,
            mb: 1,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: 1 }}>
            <TextField
              size="small"
              label="Transporter Name"
              name="transporter_name"
              value={filters.transporter_name}
              onChange={handleFilterChange}
              InputProps={{
                sx: {
                  height: "30px",
                  fontSize: "0.7rem",
                  padding: "0 8px",
                  borderRadius: "0px",
                },
              }}
              InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
              sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 22%" } }}
            />

            <TextField
              size="small"
              label="Account No"
              name="account_no"
              value={filters.account_no}
              onChange={handleFilterChange}
              InputProps={{
                sx: {
                  height: "30px",
                  fontSize: "0.7rem",
                  padding: "0 8px",
                  borderRadius: "0px",
                },
              }}
              InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
              sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 22%" } }}
            />

            <TextField
              size="small"
              label="PAN No"
              name="pan_no"
              value={filters.pan_no}
              onChange={handleFilterChange}
              InputProps={{
                sx: {
                  height: "30px",
                  fontSize: "0.7rem",
                  padding: "0 8px",
                  borderRadius: "0px",
                },
              }}
              InputLabelProps={{ sx: { fontSize: "0.7rem" } }}
              sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 22%" } }}
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleOpen}
            sx={{
              minWidth: { xs: "100%", sm: "auto" },
              whiteSpace: "nowrap",
              height: "30px",
              fontSize: "0.7rem",
              borderRadius: "0px",
              px: 2,
            }}
          >
            <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
            Transporter
          </Button>
        </Box>
      </Box>

      <Box>
        <InfiniteScroll
          dataLength={transporters.length}
          next={() => fetchData(page + 1)}
          hasMore={hasMore}
          loader={
            <Typography textAlign="center" p={2}>
              Loading...
            </Typography>
          }
          endMessage={
            <Typography textAlign="center" p={2}>
              No more transporters
            </Typography>
          }
        >
          {isMobile ? (
            <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
              {filteredTransporters.length === 0 ? (
                <Typography textAlign="center" p={3}>
                  No transporters found.
                </Typography>
              ) : (
                filteredTransporters.map((transporter, index) => (
                  <MobileTransporterCard
                    key={transporter.id}
                    transporter={transporter}
                    index={index}
                  />
                ))
              )}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                width: "100%",
                overflowX: "auto",
                height: "88vh",
                maxWidth: { xs: 350, sm: 1050, md: 1270 },
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: "#f5f5f5",
                    borderBottom: "1px solid #ccc",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      fontSize: "0.7rem",
                      color: (theme) =>
                        theme.palette.mode === "dark" ? "#2196f3" : "#333",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                    },
                  }}
                >
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {/* ✅ Checkbox */}
                    {userRole != "Staff" && (
                      <TableCell sx={{ padding: "0px" }}>Approve</TableCell>
                    )}

                    <TableCell sx={{ position: "sticky", left: 0, zIndex: 3 }}>
                      S.No.
                    </TableCell>
                    <TableCell>Transporter No</TableCell>
                    <TableCell>Transporter Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>GST</TableCell>
                    <TableCell>PAN</TableCell>
                    <TableCell>Account No</TableCell>
                    <TableCell>IFSC</TableCell>
                    <TableCell>Bank Name</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell sx={{ position: "sticky", right: 0, zIndex: 3 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredTransporters.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={userRole != "Staff" ? 13 : 12}
                        align="center"
                      >
                        No transporters found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransporters.map((t, index) => (
                      <TableRow
                        hover
                        key={t.id}
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:hover": { backgroundColor: "#e3f2fd !important" },
                          "& td": {
                            paddingTop: "2px !important",
                            paddingBottom: "2px !important",
                            fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 1050,
                            transition: "background-color 0.3s ease",
                            color: (theme) =>
                              theme.palette.mode === "dark" ? "white" : "inherit",
                            backgroundColor: (theme) =>
                              theme.palette.mode === "dark" ? "black" : "inherit",
                          },
                        }}
                      >
                        {/* ✅ Checkbox */}
                        {userRole != "Staff" && (
                          <TableCell>
                            <Checkbox
                              checked={t.status === "Approved" || t.status === "Rejected"}
                              onChange={() => {
                                setFormData({ ...t });
                                setEditingId(t.id);
                                setEditMode(true);
                                setTransporterToApprove(t);
                                setOpen(true);
                              }}
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                                transform: "scale(0.6)",
                                color:
                                  t.status === "Approved"
                                    ? "green"
                                    : t.status === "Rejected"
                                      ? "red"
                                      : "grey",
                                "&.Mui-checked": {
                                  color: t.status === "Approved" ? "green" : "red",
                                },
                              }}
                            />
                          </TableCell>
                        )}

                        <TableCell sx={{ position: "sticky", left: 0, zIndex: 3 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell>{t.id}</TableCell>
                        <TableCell>{t.transporter_name}</TableCell>
                        <TableCell>{t.contact}</TableCell>
                        <TableCell>{t.gst_no}</TableCell>
                        <TableCell>{t.pan_no}</TableCell>
                        <TableCell>{t.account_no}</TableCell>
                        <TableCell>{t.ifsc_code}</TableCell>
                        <TableCell>{t.bank_name}</TableCell>
                        <TableCell>{t.branch_name}</TableCell>
                        <TableCell>
                          <Tooltip title={t.address} arrow>
                            <span>{truncateText(t.address, 20)}</span>
                          </Tooltip>
                        </TableCell>

                        <TableCell sx={{ position: "sticky", right: 0, zIndex: 3 }}>
                          {/* ✅ Edit */}
                          <IconButton
                            sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                            size="small"
                            color="primary"
                            onClick={() => handleEditTransporter(t.id)}
                            disabled={t.status === "Approved"}
                          >
                            <Edit transform="scale(0.6)" />
                          </IconButton>

                          {/* Delete */}
                          <IconButton
                            sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                            size="small"
                            color="error"
                            onClick={(e) => {
                              setAnchorEl(e.currentTarget);
                              setSelectedId(t.id);
                            }}
                            disabled={t.status === "Approved"}
                          >
                            <Delete transform="scale(0.6)" />
                          </IconButton>

                          {/* View */}
                          <IconButton
                            sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                            size="small"
                            color="info"
                            onClick={() => handleViewOpen(t)}
                          >
                            <Visibility sx={{ transform: "scale(0.7)" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>

        {/* View Dialog */}
        <Dialog
          open={viewOpen}
          onClose={handleViewClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { maxHeight: "90vh" },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "#1976d2",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: "0.9rem", fontWeight: "bold" }}>
              Transporter Details
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            {viewTransporter && (
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "70vh", overflow: "auto", boxShadow: "none" }}
              >
                <Table size="small">
                  <TableBody>
                    {[
                      {
                        label: "Transporter Name",
                        value: viewTransporter.transporter_name,
                      },
                      { label: "Contact", value: viewTransporter.contact },
                      { label: "GST No", value: viewTransporter.gst_no },
                      { label: "PAN No", value: viewTransporter.pan_no },
                      { label: "Account No", value: viewTransporter.account_no },
                      { label: "IFSC Code", value: viewTransporter.ifsc_code },
                      { label: "Bank Name", value: viewTransporter.bank_name },
                      { label: "Branch Name", value: viewTransporter.branch_name },
                      {
                        label: "Address",
                        value: viewTransporter.address,
                        wrap: true,
                      },
                      {
                        label: "Status",
                        value: (
                          <Chip
                            label={
                              viewTransporter.status === "Approved"
                                ? "Approved"
                                : viewTransporter.status === "Rejected"
                                  ? "Rejected"
                                  : "Pending"
                            }
                            color={
                              viewTransporter.status === "Approved"
                                ? "success"
                                : viewTransporter.status === "Rejected"
                                  ? "error"
                                  : "warning"
                            }
                            variant="outlined"
                            size="small"
                          />
                        ),
                      },
                      {
                        label: "Approved By",
                        value:
                          viewTransporter.approved_by_name ||
                          viewTransporter.approved_by,
                      },
                      {
                        label: "Remark",
                        value: viewTransporter.remark,
                        wrap: true,
                      },
                    ].map((item, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                          "&:hover": {
                            bgcolor: "#e3f2fd",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            width: "35%",
                            fontSize: "0.75rem",
                            borderRight: "1px solid #e0e0e0",
                            color: "#333",
                          }}
                        >
                          {item.label}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.75rem",
                            whiteSpace: item.wrap ? "normal" : "nowrap",
                            wordBreak: item.wrap ? "break-word" : "normal",
                          }}
                        >
                          {item.value || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 1.5, bgcolor: "#f5f5f5" }}>
            <Button
              onClick={handleViewClose}
              color="primary"
              variant="contained"
              size="small"
              sx={{ fontSize: "0.75rem" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ✅ UPDATED - Add/Edit Modal with Conditional Footer */}
        <Modal
          open={open}
          onClose={handleClose}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: {
                xs: "95%",
                sm: "85%",
                md: 700,
                lg: 800,
              },
              maxHeight: {
                xs: "90vh",
                sm: "85vh",
                md: "auto",
              },
              overflowY: "auto",
              p: {
                xs: 2,
                sm: 2.5,
                md: 3,
              },
              borderRadius: {
                xs: 1,
                md: 2,
              },
            }}
          >
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {editMode ? "Edit Transporter" : "Add Transporter"}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  <TextField
                    label="Transporter Name"
                    name="transporter_name"
                    value={formData.transporter_name}
                    onChange={handleChange}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                    required
                  />

                  <TextField
                    label="Contact"
                    name="contact"
                    value={formData.contact}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        setFormData((p) => ({ ...p, contact: value }));
                      }
                    }}
                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                    required
                  />

                  <TextField
                    label="GST"
                    name="gst_no"
                    value={formData.gst_no}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        gst_no: e.target.value.toUpperCase(),
                      }))
                    }
                    onBlur={() =>
                      setGstError(
                        isValidGST(formData.gst_no)
                          ? ""
                          : "Invalid GST format (e.g., 22AAAAA0000A1Z5)"
                      )
                    }
                    error={!!gstError}
                    helperText={gstError}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                  />

                  <TextField
                    label="PAN"
                    name="pan_no"
                    value={formData.pan_no}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        pan_no: e.target.value.toUpperCase(),
                      }))
                    }
                    onBlur={() =>
                      setPanError(
                        isValidPAN(formData.pan_no)
                          ? ""
                          : "Invalid PAN format (e.g., AAAAA1234A)"
                      )
                    }
                    error={!!panError}
                    helperText={panError}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                  />

                  <TextField
                    label="Account No"
                    name="account_no"
                    value={formData.account_no}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*$/.test(value)) {
                        setFormData((p) => ({ ...p, account_no: value }));
                      }
                    }}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                    required
                  />

                  <TextField
                    label="IFSC Code"
                    name="ifsc_code"
                    value={formData.ifsc_code}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        ifsc_code: e.target.value.toUpperCase(),
                      }))
                    }
                    onBlur={() =>
                      setIfscError(
                        !formData.ifsc_code || isValidIFSC(formData.ifsc_code)
                          ? ""
                          : "Invalid IFSC (e.g., SBIN0001234)"
                      )
                    }
                    fullWidth
                    error={!!formErrors.ifsc_code}
                    helperText={formErrors.ifsc_code}
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                  />

                  <TextField
                    label="Bank Name"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleChange}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                    error={!!formErrors.bank_name}
                    helperText={formErrors.bank_name}
                  />

                  <TextField
                    label="Branch Name"
                    name="branch_name"
                    value={formData.branch_name}
                    onChange={handleChange}
                    fullWidth
                    sx={{
                      flex: {
                        xs: "1 1 100%",
                        sm: "1 1 48%",
                      },
                    }}
                    size="small"
                    error={!!formErrors.branch_name}
                    helperText={formErrors.branch_name}
                  />
                </Box>

                <TextField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={{ xs: 2, sm: 3 }}
                  size="small"
                  error={!!formErrors.address}
                  helperText={formErrors.address}
                />

                {/* ✅ CONDITIONAL FOOTER */}
                {/* ✅ CONDITIONAL FOOTER - Fixed Button Styling */}
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1, sm: 2 },
                    justifyContent: "flex-end",
                    mt: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                      borderRadius: 0,
                      height: { xs: 40, sm: 35 },
                      minWidth: { xs: "100%", sm: 100 },
                      fontSize: { xs: "0.8rem", sm: "0.7rem" },
                      order: { xs: 2, sm: 1 },
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                      borderRadius: 0,
                      height: { xs: 40, sm: 35 },
                      minWidth: { xs: "100%", sm: 120 },
                      fontSize: { xs: "0.8rem", sm: "0.7rem" },
                      order: { xs: 1, sm: 2 },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : editMode ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </Button>

                  {/* ✅ Show Approve/Reject ONLY when checkbox was clicked - SAME STYLING */}
                  {transporterToApprove && userRole != "Staff" && (
                    <>
                      <Button
                        color="success"
                        size="small"
                        onClick={() => {
                          setActionType("Approved");
                          setOpenApproveRejectModal(true);
                        }}
                        sx={{
                          borderRadius: 0,
                          height: { xs: 40, sm: 35 },
                          minWidth: { xs: "100%", sm: 100 },
                          fontSize: { xs: "0.8rem", sm: "0.7rem" },
                        }}
                      >
                        Approve
                      </Button>

                      <Button
                        color="error"
                        size="small"
                        onClick={() => {
                          setActionType("Rejected");
                          setOpenApproveRejectModal(true);
                        }}
                        sx={{
                          borderRadius: 0,
                          height: { xs: 40, sm: 35 },
                          minWidth: { xs: "100%", sm: 100 },
                          fontSize: { xs: "0.8rem", sm: "0.7rem" },
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </form>
          </Paper>
        </Modal>

        {/* ✅ NEW - Approve/Reject Modal */}
        <Dialog
          open={openApproveRejectModal}
          onClose={() => {
            setOpenApproveRejectModal(false);
            setRemarkInput("");
            setActionType(null);
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {actionType === "Approved"
              ? "Approve Transporter"
              : "Reject Transporter"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please enter a remark before{" "}
              {actionType === "Approved" ? "approving" : "rejecting"} this
              transporter:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={remarkInput}
              onChange={(e) => setRemarkInput(e.target.value)}
              placeholder="Enter remark..."
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenApproveRejectModal(false);
                setRemarkInput("");
                setActionType(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color={actionType === "Approved" ? "success" : "error"}
              variant="contained"
              onClick={handleApproveReject}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setSelectedId(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Box sx={{ p: 1 }}>
            <Typography variant="body2">Are you sure?</Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Button
                color="error"
                size="small"
                onClick={() => {
                  if (selectedId) {
                    handleDeleteTransporter(selectedId);
                    setAnchorEl(null);
                    setSelectedId(null);
                  } else {
                    toast.error("Transporter ID is missing.");
                  }
                }}
              >
                Yes
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setAnchorEl(null);
                  setSelectedId(null);
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default Transporter;